import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import './HomePage.css';
import { useStore } from '../store/useStore';
import { gameAPI } from '../services/api';
import api from '../services/api';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import lunaButton from '../assets/luna-button.png';
import usdtLogo from '../assets/usdt-logo.png';
import spermLogo from '../assets/sperm-logo.png';
import tonmoonLogo from '../assets/tonmoon-logo.png';
import RecentActivityTicker from '../components/RecentActivityTicker';
import DepositModalSimple from '../components/DepositModalSimple';
import GameModal from '../components/GameModal/GameModal';
import ClaimDetailsModal from '../components/ClaimDetailsModal/ClaimDetailsModal';
import ClaimReceiptModal from '../components/ClaimReceiptModal';

import { formatNumber, formatPercent, formatTime } from '../utils/formatters';
import {
  BackgroundBubbles,
  ParticleEffect,
  CoinCollectAnimation,
  PulseAnimation,
  StarField
} from '../components/VisualEffects';
import { useSound } from '../hooks/useSound';
import SoundManager from '../services/SoundManager';
import '../styles/animations.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, updateBalance, spermPrice } = useStore();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();

  // Звуки
  const playButtonClick = useSound('ui/ui_button_click.mp3');
  const [canClaim, setCanClaim] = useState(false);
  const [timeUntilClaim, setTimeUntilClaim] = useState('');
  const [claimMessageType, setClaimMessageType] = useState<'noStaking' | 'ready' | 'timer' | 'firstClaim' | null>(null);
  const [claimAmount, setClaimAmount] = useState(0);
  const [isFirstClaim, setIsFirstClaim] = useState(false);
  const [isClaimLoading, setIsClaimLoading] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [activityFeed, setActivityFeed] = useState<any[]>([]);
  const [isCharging, setIsCharging] = useState(false);
  const [chargeProgress, setChargeProgress] = useState(0);
  const [holdTimer, setHoldTimer] = useState<NodeJS.Timeout | null>(null);
  const [chargeInterval, setChargeInterval] = useState<NodeJS.Timeout | null>(null);
  const [miningRate, setMiningRate] = useState(0);
  const [dailyEarnings, setDailyEarnings] = useState(0);
  const [referralBonus, setReferralBonus] = useState(0);
  const [totalAPR, setTotalAPR] = useState(0);
  const [lunarAPR, setLunarAPR] = useState(0); // 🌙 APR лунного стейкинга
  const [starAPR, setStarAPR] = useState(0); // ⭐ APR звездного стейкинга
  const [marathonAPR, setMarathonAPR] = useState(0); // 🎯 APR марафона
  const [piggyBankBalance, setPiggyBankBalance] = useState(0); // 🏦 Баланс копилки SPERM
  const [balanceUsdt, setBalanceUsdt] = useState(0); // 💵 Баланс USDT
  const [piggyBankUsdt, setPiggyBankUsdt] = useState(0); // 💵 Копилка USDT
  const [promoBonusUsdt, setPromoBonusUsdt] = useState(0); // 🎟️ Промо-бонус Lunar Start (на Лунный Марафон, не выводится)
  const [isLowEndDevice, setIsLowEndDevice] = useState(false); // 📱 Определение слабого устройства
  const [showClaimAnimation, setShowClaimAnimation] = useState(false);
  const [claimedAmount, setClaimedAmount] = useState(0);
  const [coinAnimations, setCoinAnimations] = useState<{x: number, y: number, amount: number, id: string}[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Звуки claim теперь через SoundManager - refs больше не нужны
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawCurrency, setWithdrawCurrency] = useState<'SPERM' | 'USDT'>('SPERM'); // 💵 Выбор валюты вывода
  // 🔒 Anti-fraud: Лимиты вывода
  const [withdrawLimits, setWithdrawLimits] = useState({
    availableToWithdraw: 0,
    needToWager: 0,
    balance: 0
  });
  const [withdrawFuel, setWithdrawFuel] = useState({
    fuel_percent: 0, max_withdrawal: 0, slot_exempt: 0, total_staking: 0, needed_for_100: 0
  });

  // 🔐 2FA состояния для вывода
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // 🚫 ЧС выводов - полная блокировка
  const [withdrawalBlocked, setWithdrawalBlocked] = useState(false);

  const [showGameModal, setShowGameModal] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [currentDailyGame, setCurrentDailyGame] = useState<any>(null);
  const [showClaimReceipt, setShowClaimReceipt] = useState(false);
  const [claimReceiptDetails, setClaimReceiptDetails] = useState<any>(null);

  // 🌙 Состояние для анимации при нажатии на недоступную луну
  const [moonShaking, setMoonShaking] = useState(false);
  const [timerPulse, setTimerPulse] = useState(false);

  // Состояния для буста
  const [activeBoost, setActiveBoost] = useState<any>(null);
  const [boostTimeLeft, setBoostTimeLeft] = useState(0);
  const [boostedRate, setBoostedRate] = useState(0);

  // Состояние для модального окна с деталями claim
  const [showClaimDetails, setShowClaimDetails] = useState(false);
  const [lastClaimData, setLastClaimData] = useState<any>(null);

  // Настройки стейкинга для интервала claim
  const [claimInterval, setClaimInterval] = useState(24); // Часов между claim (по умолчанию 24)

  // 🌙 Состояния для кастомизации луны
  const [moonCustomization, setMoonCustomization] = useState<{
    moonImage: string;
    backgroundImage: string | null;
    effects: string[];
  } | null>(null);

  // Используем useRef для хранения интервалов
  const boostIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const boostTimerRef = useRef<NodeJS.Timeout | null>(null);
  const claimIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const claimTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Обработка переключения вкладок для корректного обновления таймеров
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        console.log('🌙 Страница скрыта - сохраняем время ухода');
        // Сохраняем время когда пользователь ушел со страницы
        localStorage.setItem('lastVisibleTime', Date.now().toString());

        // Приостанавливаем интервалы при скрытии страницы
        if (boostIntervalRef.current) {
          clearInterval(boostIntervalRef.current);
          boostIntervalRef.current = null;
        }
        if (boostTimerRef.current) {
          clearInterval(boostTimerRef.current);
          boostTimerRef.current = null;
        }
      } else {
        console.log('☀️ Страница видима - синхронизируем с сервером');

        // Получаем время когда пользователь ушел
        const lastVisibleTime = parseInt(localStorage.getItem('lastVisibleTime') || '0');
        const timeAway = lastVisibleTime > 0 ? Math.floor((Date.now() - lastVisibleTime) / 1000) : 0;
        console.log(`⏰ Пользователь отсутствовал ${timeAway} секунд`);

        // КРИТИЧНО: всегда проверяем актуальное состояние с сервера
        if (user) {
          try {
            const response = await gameAPI.getBoostStatus();
            console.log('🔄 Синхронизация буста:', response.data);

            if (response.data?.success && response.data?.data?.active) {
              const boostData = response.data.data;
              console.log('✅ Буст все еще активен:', boostData.remainingSeconds, 'сек');

              setActiveBoost({
                multiplier: boostData.multiplier,
                duration: boostData.remainingSeconds,
                expiresAt: boostData.expiresAt
              });
              setBoostTimeLeft(boostData.remainingSeconds);

              if (miningRate > 0) {
                setBoostedRate(miningRate * boostData.multiplier);
              }

              // Перезапускаем таймер обратного отсчета если буст активен
              if (boostTimerRef.current) {
                clearInterval(boostTimerRef.current);
              }

              boostTimerRef.current = setInterval(() => {
                setBoostTimeLeft((prev) => {
                  if (prev <= 1) {
                    if (boostTimerRef.current) {
                      clearInterval(boostTimerRef.current);
                      boostTimerRef.current = null;
                    }
                    // Проверяем на сервере что буст действительно истек
                    gameAPI.getBoostStatus().then(serverResponse => {
                      if (!serverResponse.data?.data?.active) {
                        console.log('❌ Буст истек - сбрасываем локально');
                        setActiveBoost(null);
                        setBoostedRate(0);
                      }
                    }).catch(err => {
                      console.error('Ошибка проверки буста:', err);
                      setActiveBoost(null);
                      setBoostedRate(0);
                    });
                    return 0;
                  }
                  return prev - 1;
                });
              }, 1000);
            } else {
              console.log('❌ Буст не активен - очищаем');
              setActiveBoost(null);
              setBoostedRate(0);
              setBoostTimeLeft(0);

              // Очищаем таймеры
              if (boostTimerRef.current) {
                clearInterval(boostTimerRef.current);
                boostTimerRef.current = null;
              }
            }
          } catch (error) {
            console.error('Ошибка синхронизации буста:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Также проверяем при фокусе на окно (для Telegram Mini App)
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [user, miningRate]);

  // 🔐 Таймер обратного отсчёта для повторной отправки кода 2FA
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 📱 Определение слабого устройства для оптимизации анимации
  useEffect(() => {
    const detectLowEndDevice = () => {
      // Проверяем prefers-reduced-motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        setIsLowEndDevice(true);
        return;
      }

      // Проверяем количество ядер процессора (если доступно)
      const cores = (navigator as any).hardwareConcurrency || 4;
      if (cores <= 2) {
        setIsLowEndDevice(true);
        return;
      }

      // Проверяем memory (если доступно)
      const memory = (performance as any).memory;
      if (memory && memory.jsHeapSizeLimit < 1000000000) { // < 1GB
        setIsLowEndDevice(true);
        return;
      }

      // Измеряем FPS для определения производительности
      let frameCount = 0;
      let lastTime = performance.now();
      const measureFPS = () => {
        frameCount++;
        const currentTime = performance.now();
        if (currentTime >= lastTime + 1000) {
          const fps = frameCount;
          frameCount = 0;
          lastTime = currentTime;

          // Если FPS < 30, считаем устройство слабым
          if (fps < 30) {
            setIsLowEndDevice(true);
            return;
          }

          // Продолжаем измерение еще 2 секунды
          if (currentTime < lastTime + 3000) {
            requestAnimationFrame(measureFPS);
          }
        } else {
          requestAnimationFrame(measureFPS);
        }
      };

      requestAnimationFrame(measureFPS);
    };

    detectLowEndDevice();
  }, []);

  // 📱 Устанавливаем data-quality атрибут для CSS оптимизации
  useEffect(() => {
    if (isLowEndDevice) {
      document.body.setAttribute('data-quality', 'low');
      console.log('📱 Слабое устройство обнаружено - включена упрощенная анимация');
    } else {
      document.body.setAttribute('data-quality', 'high');
    }
  }, [isLowEndDevice]);

  // Загрузка текущего статуса буста при монтировании
  useEffect(() => {
    const loadBoostStatus = async () => {
      try {
        const response = await gameAPI.getBoostStatus();
        console.log('🔥 BOOST STATUS RESPONSE:', response.data);

        if (response.data.success && response.data.data.active) {
          const boostData = response.data.data;
          console.log('✅ БУСТ АКТИВЕН!', boostData);

          setActiveBoost({
            multiplier: boostData.multiplier,
            duration: boostData.remainingSeconds,
            expiresAt: boostData.expiresAt
          });
          setBoostTimeLeft(boostData.remainingSeconds);

          // Вычисляем boostedRate на основе текущего miningRate
          if (miningRate > 0) {
            setBoostedRate(miningRate * boostData.multiplier);
            console.log('💰 BoostedRate установлен:', miningRate * boostData.multiplier);
          }
        } else {
          console.log('❌ Буст не активен или истек');
          // Сбрасываем буст если он истек
          setActiveBoost(null);
          setBoostedRate(0);
        }
      } catch (error) {
        console.error('Ошибка загрузки статуса буста:', error);
      }
    };

    if (user) {
      console.log('👤 User найден, загружаем буст. MiningRate:', miningRate);
      loadBoostStatus();

      // Очищаем старый интервал если он есть
      if (boostIntervalRef.current) {
        clearInterval(boostIntervalRef.current);
      }

      // Проверяем статус буста каждые 3 секунды
      boostIntervalRef.current = setInterval(loadBoostStatus, 3000);

      return () => {
        if (boostIntervalRef.current) {
          clearInterval(boostIntervalRef.current);
          boostIntervalRef.current = null;
        }
      };
    }
  }, [user, miningRate]);

  // Обработка активации буста
  useEffect(() => {
    const handleBoostActivated = async (event: CustomEvent) => {
      console.log('🎉 [HomePage] Получено событие boostActivated:', event.detail);
      const boost = event.detail;

      // Проверяем текущий miningRate
      console.log('⚡ [HomePage] Текущий miningRate:', miningRate);

      // Если miningRate = 0, загружаем актуальные данные с сервера
      let currentMiningRate = miningRate;
      if (miningRate === 0) {
        console.log('⚠️ [HomePage] MiningRate = 0, загружаем данные с сервера...');
        try {
          const response = await gameAPI.getClaimStatus();
          if (response.data.success) {
            currentMiningRate = response.data.data.miningRate || 0;
            console.log('✅ [HomePage] Загружен miningRate с сервера:', currentMiningRate);
            setMiningRate(currentMiningRate);
            setDailyEarnings(response.data.data.dailyEarnings || 0);
            setReferralBonus(response.data.data.referralBonus || 0);
            setTotalAPR(response.data.data.totalAPR || 0);
          }
        } catch (error) {
          console.error('❌ [HomePage] Ошибка загрузки miningRate:', error);
        }
      }

      console.log('⚡ [HomePage] Устанавливаем буст в state:', {
        multiplier: boost.multiplier,
        duration: boost.duration,
        miningRate: currentMiningRate,
        boostedRate: currentMiningRate * boost.multiplier
      });

      setActiveBoost(boost);
      setBoostTimeLeft(boost.duration);
      setBoostedRate(currentMiningRate * boost.multiplier);

      // Очищаем предыдущий таймер если он есть
      if (boostTimerRef.current) {
        clearInterval(boostTimerRef.current);
        boostTimerRef.current = null;
      }

      // Запускаем таймер обратного отсчета
      boostTimerRef.current = setInterval(() => {
        setBoostTimeLeft((prev) => {
          if (prev <= 1) {
            // Очищаем таймер
            if (boostTimerRef.current) {
              clearInterval(boostTimerRef.current);
              boostTimerRef.current = null;
            }

            // Проверяем на сервере что буст действительно истек
            // НО НЕ сбрасываем состояние синхронно - ждем ответа сервера
            gameAPI.getBoostStatus()
              .then(serverResponse => {
                if (!serverResponse.data.data.active) {
                  console.log('❌ Буст истек - сбрасываем состояние');
                  // Безопасно сбрасываем состояние после подтверждения сервера
                  setActiveBoost(null);
                  setBoostedRate(0);
                } else {
                  console.log('✅ Буст все еще активен на сервере');
                  // Если буст активен на сервере, обновляем время из серверного ответа
                  if (serverResponse.data.data.timeLeft > 0) {
                    setBoostTimeLeft(serverResponse.data.data.timeLeft);
                  }
                }
              })
              .catch((err) => {
                // При ошибке связи НЕ сбрасываем буст, даем пользователю продолжить
                console.error('⚠️ Ошибка проверки статуса буста:', err);
                // Оставляем буст активным, но время на 0
                console.log('⚠️ Буст остается активным до следующей успешной проверки');
              });

            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    window.addEventListener('boostActivated' as any, handleBoostActivated);

    return () => {
      window.removeEventListener('boostActivated' as any, handleBoostActivated);
      // Очищаем таймер при размонтировании
      if (boostTimerRef.current) {
        clearInterval(boostTimerRef.current);
        boostTimerRef.current = null;
      }
    };
  }, [miningRate]);
  
  // Функция воспроизведения звука
  const playSound = (type: string) => {
    try {
      // В будущем здесь будут настоящие звуки
      // Пока используем Web Audio API для генерации звуков
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Настройки звука в зависимости от типа эффекта
      switch(type) {
        case 'golden_harvest':
          oscillator.frequency.value = 800;
          gainNode.gain.value = 0.3;
          break;
        case 'lunar_blessing':
          oscillator.frequency.value = 1200;
          gainNode.gain.value = 0.4;
          break;
        case 'time_warp':
          oscillator.frequency.value = 600;
          gainNode.gain.value = 0.2;
          break;
        default:
          oscillator.frequency.value = 440;
          gainNode.gain.value = 0.1;
      }
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (e) {

    }
  };

  // Загрузка фида активности и текущей игры дня
  useEffect(() => {
    // Загружаем информацию о текущей игре дня
    loadDailyGame();
  }, []);
  
  // Загрузка текущей игры дня
  const loadDailyGame = async () => {
    try {
      const response = await gameAPI.getAllGames();
      if (response.data.success && response.data.games.length > 0) {
        // Находим текущую игру дня
        const dailyGame = response.data.games.find((game: any) => game.isCurrentGame);
        if (dailyGame) {
          setCurrentDailyGame(dailyGame);
          setSelectedGameId(dailyGame.id);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки игры дня:', error);
    }
  };

  // Загрузка настроек стейкинга для интервала claim
  const fetchStakingSettings = async () => {
    try {
      const response = await api.get('/staking/settings');
      if (response.data.success) {
        const interval = response.data.lunar_claim_interval || 24;
        setClaimInterval(interval);
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек стейкинга:', error);
      // Оставляем значение по умолчанию 24
    }
  };

  // Загружаем статус claim из БД
  useEffect(() => {
    const fetchClaimStatus = async () => {
      try {
        const response = await gameAPI.getClaimStatus();
        
        if (response.data.success) {
          const data = response.data.data;
          console.log('📊 APR данные с API:', {
            lunarAPR: data.lunarAPR,
            starAPR: data.starAPR,
            marathonAPR: data.marathonAPR,
            totalAPR: data.totalAPR
          });
          setCanClaim(data.canClaim);
          setProgressPercent(data.progressPercent);
          setClaimAmount(data.accumulatedAmount);

          // Обновляем метрики из API
          setMiningRate(data.miningRate || 0);
          setDailyEarnings(data.dailyEarnings || 0);
          setReferralBonus(data.referralBonus || 0);

          // Используем APR из API (уже рассчитан на бэкенде)
          setTotalAPR(data.totalAPR || 0);
          setLunarAPR(data.lunarAPR || 0);
          setStarAPR(data.starAPR || 0);
          setMarathonAPR(data.marathonAPR || 0);

          // 🏦 Обновляем баланс копилки
          setPiggyBankBalance(data.piggyBankBalance || 0);
          // 💎 Обновляем балансы TON
          setBalanceUsdt(data.balanceUsdt || 0);
          setPiggyBankUsdt(data.piggyBankUsdt || 0);
          // 🎟️ Промо-бонус Lunar Start (отдельный эндпоинт)
          api.get('/promo/balance')
            .then(r => { if (r.data?.success) setPromoBonusUsdt(parseFloat(r.data.data.promoBonusUsdt) || 0); })
            .catch(() => {});

          // Обновляем время до следующего сбора
          setIsFirstClaim(data.isFirstClaim || false);

          if (data.noStakingMessage) {
            // Если нет стейкинга - показываем сообщение (переводим)
            setClaimMessageType('noStaking');
            setTimeUntilClaim(t('home.noStaking'));
          } else if (data.isFirstClaim) {
            // Первый Claim - показываем специальное сообщение
            setClaimMessageType('firstClaim');
            setTimeUntilClaim(t('home.firstClaim'));
          } else if (data.canClaim) {
            setClaimMessageType('ready');
            setTimeUntilClaim(t('home.readyShort'));
          } else if (data.timeUntilNextClaim > 0) {
            setClaimMessageType('timer');

            // Гасим предыдущий тик. Без этого они копились каждые 10 секунд
            // и лавиной дёргали claim-status (флуд 02.08.2026)
            if (claimTimerRef.current) {
              clearInterval(claimTimerRef.current);
              claimTimerRef.current = null;
            }

            const nextClaim = new Date(data.nextClaimTime).getTime();

            // Рисует оставшееся время и возвращает его в мс
            const renderTimer = () => {
              const timeLeft = Math.max(0, nextClaim - new Date().getTime());

              if (timeLeft > 0) {
                const hours = Math.floor(timeLeft / (1000 * 60 * 60));
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
                setTimeUntilClaim(`${hours}ч ${minutes}м ${seconds}с`);
              }

              return timeLeft;
            };

            // Тик заводим только если время реально осталось — иначе
            // (расхождение часов клиента и сервера) ушли бы в рекурсию
            if (renderTimer() > 0) {
              claimTimerRef.current = setInterval(() => {
                if (renderTimer() > 0) return;

                // Время вышло: гасим тик и один раз обновляем статус
                if (claimTimerRef.current) {
                  clearInterval(claimTimerRef.current);
                  claimTimerRef.current = null;
                }
                fetchClaimStatus();
              }, 1000);
            }
          }
        }
      } catch (error) {

        // Fallback на локальный расчет если API недоступен
        if (!user?.lastClaim) {
          // Новый пользователь - начинаем с нуля
          setCanClaim(false);
          setClaimAmount(0);
          setProgressPercent(0);
          // Используем интервал из настроек (claimInterval часов)
          setTimeUntilClaim(`${claimInterval}:00:00`);
        }
      }
    };

    if (user) {
      fetchStakingSettings(); // Загружаем настройки интервала claim
      fetchClaimStatus();

      // Очищаем старый интервал если он есть
      if (claimIntervalRef.current) {
        clearInterval(claimIntervalRef.current);
      }

      // Обновляем статус каждые 10 секунд
      claimIntervalRef.current = setInterval(fetchClaimStatus, 10000);

      return () => {
        if (claimIntervalRef.current) {
          clearInterval(claimIntervalRef.current);
          claimIntervalRef.current = null;
        }
        if (claimTimerRef.current) {
          clearInterval(claimTimerRef.current);
          claimTimerRef.current = null;
        }
      };
    }
  }, [user]);

  // Реагируем на изменение языка для обновления статических сообщений
  useEffect(() => {
    if (claimMessageType === 'noStaking') {
      setTimeUntilClaim(t('home.noStaking'));
    } else if (claimMessageType === 'firstClaim') {
      setTimeUntilClaim(t('home.firstClaim'));
    } else if (claimMessageType === 'ready') {
      setTimeUntilClaim(t('home.readyShort'));
    }
  }, [i18n.language, claimMessageType, t]);

  // 🌙 Загружаем настройки кастомизации луны
  useEffect(() => {
    const loadMoonCustomization = async () => {
      try {
        const response = await api.get('/achievements/customization');
        if (response.data.success) {
          const data = response.data.data;
          // Получаем картинки из каталогов
          const catalogResponse = await api.get('/achievements/catalogs');
          if (catalogResponse.data.success) {
            const colors = catalogResponse.data.data.colors;
            const backgrounds = catalogResponse.data.data.backgrounds;
            const effects = catalogResponse.data.data.effects;

            const selectedColor = colors.find((c: any) => c.id === data.moonColor);
            const selectedBg = backgrounds.find((b: any) => b.id === data.backgroundId);
            const activeEffects = effects
              .filter((e: any) => data.moonEffects.includes(e.id))
              .map((e: any) => e.code);

            setMoonCustomization({
              moonImage: selectedColor?.imagePath || '/assets/moons/moon-1.png',
              backgroundImage: selectedBg?.imagePath || null,
              effects: activeEffects || []
            });
          }
        }
      } catch (error) {
        // Кастомизация недоступна - используем стандартную луну
      }
    };

    if (user) {
      loadMoonCustomization();
    }
  }, [user]);

  // Звуки claim теперь управляются через SoundManager - useEffect больше не нужен

  // Начало удержания (зарядка луны)
  const handleMouseDown = () => {
    if (!canClaim || isClaimLoading) return;

    // Воспроизводим звук клика через SoundManager
    SoundManager.playClaimClick();

    // Тактильная обратная связь - 3 коротких вибрации
    try {
      if ((window as any).Telegram?.WebApp?.HapticFeedback) {
        (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
        setTimeout(() => (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light'), 100);
        setTimeout(() => (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light'), 200);
      }
    } catch (e) {

    }

    // 🚀 Для первого клейма - мгновенная активация без удержания
    if (isFirstClaim) {
      handleClaimComplete();
      return;
    }

    setIsCharging(true);
    setChargeProgress(0);

    // Интервал для плавного увеличения прогресса
    const interval = setInterval(() => {
      setChargeProgress(prev => {
        if (prev >= 100) {
          return 100;
        }
        return prev + 3.33; // Увеличиваем на 3.33% каждые 100мс (3 секунды = 100%)
      });
    }, 100);

    setChargeInterval(interval);

    // Таймер на 3 секунды для завершения сбора
    const timer = setTimeout(() => {
      handleClaimComplete();
    }, 3000);

    setHoldTimer(timer);
  };
  
  // Отпускание кнопки (прерывание зарядки)
  const handleMouseUp = () => {
    if (!isCharging) return;
    
    // Очищаем таймеры
    if (holdTimer) {
      clearTimeout(holdTimer);
      setHoldTimer(null);
    }
    
    if (chargeInterval) {
      clearInterval(chargeInterval);
      setChargeInterval(null);
    }
    
    // Сбрасываем состояние
    setIsCharging(false);
    setChargeProgress(0);
  };
  
  // Завершение сбора после успешной зарядки
  const handleClaimComplete = async () => {
    // Очищаем интервал
    if (chargeInterval) {
      clearInterval(chargeInterval);
      setChargeInterval(null);
    }
    
    // Тактильная обратная связь - 6 коротких вибраций при успехе
    try {
      if ((window as any).Telegram?.WebApp?.HapticFeedback) {
        for (let i = 0; i < 6; i++) {
          setTimeout(() => (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('medium'), i * 100);
        }
      }
    } catch (e) {

    }

    // Воспроизводим звук успеха через SoundManager
    SoundManager.playClaimSuccess();

    setIsClaimLoading(true);
    setIsCharging(false);
    
    try {
      const response = await gameAPI.claimRewards();

      if (response.data.success) {
        const claimedData = response.data.data;
        const actualAmount = parseFloat(claimedData.claimedAmount) || claimAmount;

        // Сохраняем данные для модального окна с реальными бонусами из backend
        setLastClaimData({
          baseAmount: parseFloat(claimedData.baseAmount) || 0,
          multiplier: parseFloat(claimedData.multiplier) || 1,
          effects: claimedData.effects || [],
          bonuses: {
            vip: claimedData.bonuses?.vip || '0.0%',
            vipName: claimedData.bonuses?.vipName || '',
            vipAmount: claimedData.bonuses?.vipAmount || 0,
            rank: claimedData.bonuses?.rank || '0.0%',
            rankName: claimedData.bonuses?.rankName || '',
            rankAmount: claimedData.bonuses?.rankAmount || 0,
            leader: claimedData.bonuses?.leader || '0.0%',
            leaderName: claimedData.bonuses?.leaderName || '',
            leaderAmount: claimedData.bonuses?.leaderAmount || 0,
            boost: claimedData.bonuses?.boost || '0.0%',
            boostAmount: claimedData.bonuses?.boostAmount || 0
          },
          claimedAmount: actualAmount
        });

        // Показываем мини-квитанцию с деталями claim
        if (claimedData.claimDetails) {
          setClaimReceiptDetails(claimedData.claimDetails);
          setTimeout(() => {
            setShowClaimReceipt(true);
          }, 5500); // 🏦 Увеличена задержка до 5.5 секунд (чтобы анимация успела закончиться)
        }

        // Уведомление о повышении ранга
        if (claimedData.rankIncreased && claimedData.rankLevel) {
          setTimeout(() => {
            alert(`${t('home.congratsRank')} ${t(`ranks.${claimedData.rankLevel}`)}!\n\n${t('home.couponReceived')}`);
          }, 2000);
        }

        // УБРАНО: Автоматическое открытие модального окна с деталями
        // setTimeout(() => {
        //   setShowClaimDetails(true);
        // }, 1000);

        // Сохраняем сумму для анимации
        setClaimedAmount(actualAmount);

        // ✅ СИНХРОНИЗАЦИЯ: Обновляем прогресс-бар чтобы показывал ту же сумму что и анимация
        setClaimAmount(actualAmount);

        // ✨ АНИМАЦИЯ СБОРА - перелет монеты из шкалы прогресса в копилку
        const progressBarElement = document.querySelector('.claim-progress');
        const piggyElement = document.querySelector('.piggybank-icon');

        console.log('🔍 Animation elements:', { progressBarElement, piggyElement });

        if (progressBarElement && piggyElement) {
          // Получаем координаты
          const startRect = progressBarElement.getBoundingClientRect();
          const endRect = piggyElement.getBoundingClientRect();

          // Стартуем из центра шкалы прогресса
          const startX = startRect.left + startRect.width / 2;
          const startY = startRect.top + startRect.height / 2;
          // Финиш в центре копилки
          const endX = endRect.left + endRect.width / 2;
          const endY = endRect.top + endRect.height / 2;

          console.log('🚀 Flying animation coordinates:', { startX, startY, endX, endY, amount: actualAmount });

          // Создаем контейнер для анимации (монета + текст)
          const flyingContainer = document.createElement('div');
          flyingContainer.innerHTML = `
            <span style="font-size: 36px; margin-right: 8px;">🪙</span>
            <span>+${actualAmount.toFixed(2)}</span>
          `;
          flyingContainer.style.cssText = `
            position: fixed;
            left: ${startX}px;
            top: ${startY}px;
            font-size: 24px;
            font-weight: 700;
            color: #FFD700;
            text-shadow: 0 0 20px rgba(255, 215, 0, 1), 0 0 10px rgba(255, 215, 0, 0.8);
            z-index: 999999;
            pointer-events: none;
            transform: translate(-50%, -50%) scale(1.2);
            white-space: nowrap;
            display: flex;
            align-items: center;
            opacity: 1;
          `;
          document.body.appendChild(flyingContainer);

          // Начальная вспышка - монета появляется и увеличивается
          setTimeout(() => {
            flyingContainer.style.transform = 'translate(-50%, -50%) scale(1.8)';
            flyingContainer.style.transition = 'transform 0.5s ease-out';
          }, 50);

          // Небольшая пауза перед полётом - монета "зависает"
          setTimeout(() => {
            flyingContainer.style.transform = 'translate(-50%, -50%) scale(1.5)';
            flyingContainer.style.transition = 'transform 0.3s ease-in-out';
          }, 600);

          // Вычисляем середину пути
          const midX = startX + (endX - startX) / 2;
          const midY = startY + (endY - startY) / 2;

          // Первая половина пути - монета остается большой (scale 1.5)
          setTimeout(() => {
            flyingContainer.style.transition = 'left 2s cubic-bezier(0.22, 0.61, 0.36, 1), top 2s cubic-bezier(0.22, 0.61, 0.36, 1)';
            flyingContainer.style.left = `${midX}px`;
            flyingContainer.style.top = `${midY}px`;
            // Размер остается большой
            flyingContainer.style.transform = 'translate(-50%, -50%) scale(1.5)';
          }, 1000);

          // Вторая половина пути - монета уменьшается до копилки
          setTimeout(() => {
            flyingContainer.style.transition = 'all 2s cubic-bezier(0.22, 0.61, 0.36, 1)';
            flyingContainer.style.left = `${endX}px`;
            flyingContainer.style.top = `${endY}px`;
            flyingContainer.style.transform = 'translate(-50%, -50%) scale(0.4)';
            flyingContainer.style.opacity = '0.9';
          }, 3000);

          // Удаляем элемент после завершения полёта
          setTimeout(() => {
            flyingContainer.style.opacity = '0';
            flyingContainer.style.transition = 'opacity 0.3s ease-out';
          }, 4800);

          setTimeout(() => {
            flyingContainer.remove();
          }, 5200);

          // Вспышка и увеличение копилки при получении монет
          setTimeout(() => {
            const piggy = piggyElement as HTMLElement;

            // Добавляем класс для анимации
            piggy.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease';
            piggy.style.transform = 'scale(1.5)';
            piggy.style.filter = 'drop-shadow(0 0 15px rgba(255, 215, 0, 0.9))';

            setTimeout(() => {
              piggy.style.transform = 'scale(1)';
              piggy.style.filter = '';
              setTimeout(() => {
                piggy.style.transition = '';
              }, 300);
            }, 300);
          }, 4700);
        } else {
          console.warn('⚠️ Animation elements not found!');
        }

        // 🏦 Обновляем баланс КОПИЛКИ
        if (claimedData.newPiggyBalance) {
          setPiggyBankBalance(parseFloat(claimedData.newPiggyBalance));
        } else {
          setPiggyBankBalance(prev => prev + actualAmount);
        }
        setCanClaim(false);

        // Обнуляем прогресс через 4 секунды
        setTimeout(() => {
          setProgressPercent(0);
          setChargeProgress(0);
        }, 4000);
        
        // Обновляем lastClaim для корректного отсчета
        if (user) {
          user.lastClaim = new Date();
        }
      } else {

      }
    } catch (error) {

    } finally {
      setIsClaimLoading(false);
      setChargeProgress(0);
    }
  };

  // 🔒 Загрузка лимитов вывода
  const loadWithdrawLimits = async () => {
    try {
      const response = await api.get('/wallet/available-withdraw');
      if (response.data.success && response.data.data) {
        setWithdrawLimits({
          availableToWithdraw: response.data.data.availableToWithdraw || 0,
          needToWager: response.data.data.needToWager || 0,
          balance: response.data.data.balance || 0
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки лимитов вывода:', error);
    }
  };

  // 🚫 Проверка статуса ЧС выводов
  const checkWithdrawalBlacklist = async () => {
    try {
      const response = await api.get('/wallet/blacklist-status');
      if (response.data.success) {
        setWithdrawalBlocked(response.data.blacklisted || false);
      }
    } catch (error) {
      console.error('Ошибка проверки статуса ЧС:', error);
    }
  };

  // Открытие модалки вывода с загрузкой лимитов
  const openWithdrawModal = async () => {
    playButtonClick();
    checkWithdrawalBlacklist();
    loadWithdrawLimits();
    // Загружаем Moon Fuel данные
    try {
      const fuelRes = await api.get('/wallet/fuel');
      if (fuelRes.data.success) {
        const d = fuelRes.data.data;
        setWithdrawFuel({
          fuel_percent: d.fuel_percent || 0,
          max_withdrawal: d.max_withdrawal || 0,
          slot_exempt: d.slot_exempt || 0,
          total_staking: d.total_staking || 0,
          needed_for_100: d.needed_for_100 || 0
        });
      }
    } catch (e) {}
    setShowWithdrawModal(true);
  };

  // 🔐 Шаг 1: Открыть модалку 2FA для подтверждения вывода
  const handleWithdraw = async () => {
    // Проверяем подключение кошелька
    if (!address) {
      alert(t('walletPage.alertConnectWallet') || 'Connect wallet to withdraw');
      // Открываем модальное окно подключения кошелька
      tonConnectUI.openModal();
      return;
    }

    const amount = parseFloat(withdrawAmount);

    // Проверка минимальной суммы
    const minAmount = withdrawCurrency === 'USDT' ? 10 : 10;
    if (!amount || amount < minAmount) {
      alert(`Minimum withdrawal: ${minAmount} ${withdrawCurrency}`);
      return;
    }

    // Проверка баланса
    const maxBalance = withdrawCurrency === 'USDT' ? balanceUsdt : (user?.balance || 0);
    if (amount > maxBalance) {
      alert(`Insufficient ${withdrawCurrency} balance`);
      return;
    }

    // Для SPERM проверяем availableToWithdraw
    if (withdrawCurrency === 'SPERM' && amount > withdrawLimits.availableToWithdraw) {
      alert(`Available to withdraw: ${withdrawLimits.availableToWithdraw.toFixed(2)} SPERM`);
      return;
    }

    // 🔐 Открываем модалку 2FA вместо прямого вывода
    setCodeError('');
    setVerificationCode('');
    setCodeSent(false);
    setShow2FAModal(true);
  };

  // 🔐 Шаг 2: Запросить код подтверждения в Telegram
  const requestWithdrawalCode = async () => {
    // Блокируем повторные нажатия — cooldown через countdown
    if (countdown > 0) return;
    setWithdrawLoading(true);
    setCodeError('');

    try {
      const response = await api.post('/wallet/request-withdrawal-code', {
        amount: parseFloat(withdrawAmount),
        toAddress: address,
        currency: withdrawCurrency
      });

      if (response.data.success) {
        setCodeSent(true);
        setCountdown(60);
      }
    } catch (error: any) {
      console.error('Ошибка запроса кода:', error);
      const data = error.response?.data;
      // §06.09.2026: код уже улетел в Telegram (например, оборвалась сеть на первом
      // запросе) — открываем поле ввода, а не оставляем человека на форме без него.
      if (data?.codeAlreadySent) setCodeSent(true);
      setCodeError(data?.message || data?.error || 'Ошибка отправки кода');
      // Cooldown даже при ошибке — защита от спама
      setCountdown(30);
    } finally {
      setWithdrawLoading(false);
    }
  };

  // 🔐 Шаг 3: Подтвердить вывод с кодом
  const confirmWithdrawal = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setCodeError('Введите 6-значный код');
      return;
    }

    setWithdrawLoading(true);
    setCodeError('');

    try {
      const endpoint = withdrawCurrency === 'USDT' ? '/wallet/withdraw-usdt' : '/wallet/withdraw';
      const response = await api.post(endpoint, {
        amount: parseFloat(withdrawAmount),
        toAddress: address,
        code: verificationCode
      });

      if (response.data.success) {
        setShow2FAModal(false);
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        setVerificationCode('');
        alert(`Заявка на вывод ${withdrawCurrency} создана! Обработка в течение 48 часов.`);

        // Обновляем баланс (для SPERM)
        if (withdrawCurrency === 'SPERM') {
          updateBalance((user?.balance || 0) - parseFloat(withdrawAmount));
        }
        // Для USDT баланс обновится при следующем запросе claim-status
      }
    } catch (error: any) {
      console.error(`Ошибка вывода ${withdrawCurrency}:`, error);
      setCodeError(error.response?.data?.message || error.response?.data?.error || 'Неверный код или ошибка вывода');
    } finally {
      setWithdrawLoading(false);
    }
  };

  // 🌙 Обработчик нажатия на недоступную луну - покачивание + пульс времени
  const handleMoonClick = () => {
    if (canClaim) return; // Если луна готова - не реагируем

    // Запускаем анимацию покачивания луны
    setMoonShaking(true);

    // 3 короткие вибрации
    try {
      if ((window as any).Telegram?.WebApp?.HapticFeedback) {
        const haptic = (window as any).Telegram.WebApp.HapticFeedback;
        haptic.impactOccurred('light');
        setTimeout(() => haptic.impactOccurred('light'), 100);
        setTimeout(() => haptic.impactOccurred('light'), 200);
      }
    } catch (e) {}

    // Убираем покачивание через 1 секунду
    setTimeout(() => {
      setMoonShaking(false);
    }, 1000);

    // Пульс времени начинается через 1 секунду (после покачивания)
    setTimeout(() => {
      setTimerPulse(true);
      // Убираем пульс через 0.5 секунды
      setTimeout(() => {
        setTimerPulse(false);
      }, 500);
    }, 1000);
  };

  // Обработка для мобильных устройств
  const handleTouchStart = handleMouseDown;
  const handleTouchEnd = handleMouseUp;

  return (
    <div className={`home-page${localStorage.getItem('webauth') === 'true' ? ' web-mode' : ''}`}>
      {/* 🎨 Фон из кастомизации на всю страницу (50% прозрачности, слой внизу) */}
      {moonCustomization?.backgroundImage && (
        <div
          className="custom-background-fullpage"
          style={{
            backgroundImage: `url(${moonCustomization.backgroundImage})`,
          }}
        />
      )}

      {/* Moon Fuel убран */}

      {/* Фоновые эффекты */}
      <StarField />
      <BackgroundBubbles />

      <div className="page-content fade-in">
        {/* Баланс пользователя */}
        <div className="balance-card slide-in hover-lift">
          <button className="balance-btn deposit-btn" onClick={() => { playButtonClick(); setShowDepositModal(true); }}>
            <span className="btn-icon">💳</span>
            <span className="btn-text">{t('home.depositBtn')}</span>
          </button>

          <div className="balance-content">
            <div className="balance-row">
              <img src={tonmoonLogo} alt="SPERM" className="sperm-logo-small" />
              <span className="balance-amount">{formatNumber(user?.balance || 0, 1)} SPERM</span>
              <span className="balance-divider">|</span>
              <span className="balance-usd">${((user?.balance || 0) * spermPrice).toFixed(1)}</span>
            </div>

            <div className="piggybank-row">
              <span className="piggybank-icon">🐷</span>
              <span className="piggybank-amount">{formatNumber(piggyBankBalance, 2)} SPERM</span>
              <span className="piggybank-divider">|</span>
              <span className="piggybank-usd">${(piggyBankBalance * spermPrice).toFixed(1)}</span>
            </div>

            <div className="usdt-balance-row">
              <img src={usdtLogo} alt="USDT" className="usdt-logo-small" />
              <span className="usdt-balance-amount">{formatNumber(balanceUsdt + promoBonusUsdt, 2)} USDT</span>
            </div>
          </div>

          <button className="balance-btn withdraw-btn" onClick={openWithdrawModal}>
            <span className="btn-icon">💸</span>
            <span className="btn-text">{t('home.withdrawBtn')}</span>
          </button>
        </div>

        {/* Кнопка CLAIM */}
        <div className="claim-section">
          <div className="claim-info">
            {claimMessageType === 'firstClaim' ? (
              <p className="claim-ready-inline first-claim-message">🚀 {t('home.firstClaim')}</p>
            ) : claimMessageType === 'ready' ? (
              <p className="claim-ready-inline">{t('home.ready')} <span className="claim-amount-inline">+{formatNumber(claimAmount, 4)} SPERM</span></p>
            ) : (
              <>
                <p className="claim-timer-label">{t('home.nextClaimLabel')}</p>
                <p className={`claim-timer ${timerPulse ? 'pulse-scale' : ''}`}>{timeUntilClaim}</p>
              </>
            )}
          </div>

          <div
            className={`claim-button-wrapper ${canClaim ? 'ready' : ''} ${isCharging ? 'charging' : ''} ${moonShaking ? 'shaking' : ''} hover-scale`}
            onMouseDown={canClaim ? handleMouseDown : undefined}
            onMouseUp={canClaim ? handleMouseUp : undefined}
            onMouseLeave={canClaim ? handleMouseUp : undefined}
            onTouchStart={canClaim ? handleTouchStart : undefined}
            onTouchEnd={canClaim ? handleTouchEnd : undefined}
            onClick={!canClaim ? handleMoonClick : undefined}
            onContextMenu={(e) => e.preventDefault()}
            style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
          >
            {/* Круг прогресса зарядки */}
            {isCharging && (
              <svg className="charge-progress-ring" viewBox="0 0 214 214">
                <circle
                  className="charge-progress-bg"
                  cx="107"
                  cy="107"
                  r="97"
                  strokeWidth="10"
                  fill="none"
                />
                <circle
                  className="charge-progress-fill"
                  cx="107"
                  cy="107"
                  r="97"
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 97}`}
                  strokeDashoffset={`${2 * Math.PI * 97 * (1 - chargeProgress / 100)}`}
                />
              </svg>
            )}
            
            {/* 🌙 Кастомная луна */}
            {moonCustomization ? (
              <div
                className={`luna-button-wrapper ${moonCustomization.effects.includes('particles') ? 'effect-particles' : ''} ${moonCustomization.effects.includes('pulse') ? 'effect-pulse' : ''}`}
              >
                <img
                  src={moonCustomization.moonImage}
                  alt="Moon"
                  className={`luna-button ${canClaim ? 'ready' : ''} ${isCharging ? 'charging' : ''} ${isClaimLoading ? 'claiming' : ''}`}
                />
              </div>
            ) : (
              <img
                src={lunaButton}
                alt="Luna"
                className={`luna-button ${canClaim ? 'ready' : ''} ${isCharging ? 'charging' : ''} ${isClaimLoading ? 'claiming' : ''}`}
              />
            )}
            
            {/* Текст подсказка - убрана по запросу */}
            
            {/* Процент зарядки */}
            {isCharging && (
              <div className="charge-percent">{Math.round(chargeProgress)}%</div>
            )}
            
            {canClaim && !isCharging && <div className="claim-button-glow"></div>}
          </div>

          {/* Прогресс-бар до следующего сбора */}
          <div className="progress-wrapper" style={{ position: 'relative' }}>
            {activeBoost && boostTimeLeft > 0 && (
              <div className="boost-timer-main">
                ⚡ {t('home.boost')} +{((activeBoost.multiplier - 1) * 100).toFixed(0)}% - {boostTimeLeft} {t('home.boostSeconds')}
              </div>
            )}
            <div className={`claim-progress ${activeBoost && boostTimeLeft > 0 ? 'boost-active' : ''} ${progressPercent >= 100 ? 'max-reached' : ''}`}>
              <div className="progress-header">
                <span className="progress-label">
                  {progressPercent >= 100 ? '🔥 MAX ACCUMULATION 🔥' : t('home.progress')}
                </span>
              </div>
              <div className="progress-container">
                <div className="progress-bar-bg">
                  <div
                    className={`progress-bar-fill ${activeBoost ? 'boosted' : ''} ${progressPercent >= 100 ? 'max-fill' : ''}`}
                    style={{ width: `${Math.min(progressPercent, 100)}%` }}
                  ></div>
                  <span className="progress-percent">
                    {progressPercent >= 100 ? '🏆 MAX 🏆' : `${Math.round(progressPercent)}%`}
                  </span>
                </div>
              </div>
              <div className="progress-footer">
                <span className="progress-status">
                  {t('home.accumulated')}: {formatNumber(claimAmount, 4)} SPERM
                  {progressPercent >= 100 && <span className="max-indicator"> (24+ h)</span>}
                </span>
              </div>
            </div>
          </div>
        </div>


        {/* Статистика - 4 блока как в ТЗ */}
        <div className="stats-grid">
          <div className="home-stat-card">
            <span className="home-stat-icon">💰</span>
            <div className="home-stat-content">
              <div className="home-stat-value">{formatNumber(dailyEarnings, 2)}</div>
              <div className="home-stat-label">{t('home.dailyEarnings').toUpperCase()}</div>
            </div>
          </div>
          <div className="home-stat-card">
            <span className="home-stat-icon">👥</span>
            <div className="home-stat-content">
              <div className="home-stat-value">{formatNumber(referralBonus, 2)}</div>
              <div className="home-stat-label">{t('home.referralBonus').toUpperCase()}</div>
            </div>
          </div>
          <div className={`home-stat-card ${activeBoost ? 'boost-active' : ''}`}>
            <span className="home-stat-icon">⛏️</span>
            <div className="home-stat-content">
              <div className={`home-stat-value ${activeBoost ? 'boosted-text' : ''}`}>
                {formatNumber(activeBoost ? boostedRate : miningRate, 3)}{t('home.perHour')}
                {activeBoost && (
                  <span className="boost-badge">+{((activeBoost.multiplier - 1) * 100).toFixed(0)}%</span>
                )}
              </div>
              <div className="home-stat-label">{t('home.miningRate').toUpperCase()}</div>
            </div>
          </div>
          <div className="home-stat-card apr-card">
            <div className="home-stat-content">
              <div className="apr-row">
                <span className="apr-icon">🌙</span>
                <span className="apr-value">{formatPercent(lunarAPR, 2)}</span>
              </div>
              <div className="apr-row">
                <span className="apr-icon">⭐</span>
                <span className="apr-value">{formatPercent(starAPR, 2)}</span>
              </div>
              <div className="apr-row">
                <span className="apr-icon">🎯</span>
                <span className="apr-value">{formatPercent(marathonAPR, 2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Бегущая строка с последними действиями - внизу страницы */}
        <RecentActivityTicker />
      </div>
      
      
      {/* Анимации монет при сборе */}
      {coinAnimations.map(anim => (
        <CoinCollectAnimation
          key={anim.id}
          amount={anim.amount}
          x={anim.x}
          y={anim.y}
        />
      ))}
      {/* Модальное окно пополнения с TON Connect */}
      <DepositModalSimple 
        isOpen={showDepositModal}
        onClose={() => { playButtonClick(); setShowDepositModal(false); }}
        telegramId={user?.telegramId || ''}
        onSuccess={() => {
          // Перезагружаем данные пользователя после депозита
          console.log('Депозит успешно отправлен!');
          // Можно добавить обновление баланса через API
        }}
      />
      
      {/* Модальное окно вывода */}
      {showWithdrawModal && (
        <div className="modal-overlay" onClick={() => { playButtonClick(); setShowWithdrawModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('home.withdrawTitle')}</h2>
              <button className="modal-close" onClick={() => { playButtonClick(); setShowWithdrawModal(false); }}>✕</button>
            </div>
            <div className="modal-body">
              {/* 🚫 ЧС - полная блокировка вывода */}
              {withdrawalBlocked ? (
                <div className="withdraw-blocked-box blacklisted">
                  <div className="blocked-icon">🚫</div>
                  <h3>{t('walletPage.withdrawBlacklistedTitle', 'Вывод недоступен')}</h3>
                  <p>{t('walletPage.withdrawBlacklistedDesc', 'Функция вывода временно недоступна для вашего аккаунта.')}</p>
                </div>
              ) : (
                <>
                  {/* 💵 Табы выбора валюты */}
                  <div className="withdraw-currency-tabs">
                    <button
                      className={`currency-tab ${withdrawCurrency === 'SPERM' ? 'active' : ''}`}
                      onClick={() => { playButtonClick(); setWithdrawCurrency('SPERM'); setWithdrawAmount(''); }}
                    >
                      <img src={tonmoonLogo} alt="SPERM" className="currency-tab-icon" />
                      SPERM
                    </button>
                    <button
                      className={`currency-tab ${withdrawCurrency === 'USDT' ? 'active' : ''}`}
                      onClick={() => { playButtonClick(); setWithdrawCurrency('USDT'); setWithdrawAmount(''); }}
                      // §25.10.2026: вывод USDT марафона закрыт (бэк тоже отказывает)
                      disabled
                    >
                      <img src={usdtLogo} alt="USDT" className="currency-tab-icon" />
                      USDT
                    </button>
                  </div>

                  {/* Содержимое для SPERM */}
                  {withdrawCurrency === 'SPERM' && (
                <>
                  {/* 🔒 Показываем лимиты вывода */}
                  <div className="withdraw-limits-box">
                    <div className="limit-item">
                      <span className="limit-label">{t('walletPage.withdrawBalanceLabel')}</span>
                      <span className="limit-value">{formatNumber(withdrawLimits.balance, 2)} SPERM</span>
                    </div>
                    <div className="limit-item highlight">
                      <span className="limit-label">{t('walletPage.withdrawAvailableLabel')}</span>
                      <span className="limit-value available">{formatNumber(withdrawLimits.availableToWithdraw, 2)} SPERM</span>
                    </div>
                    {withdrawLimits.needToWager > 0 && (
                      <div className="limit-item warning">
                        <span className="limit-label">{t('walletPage.withdrawNeedWager')}</span>
                        <span className="limit-value need-wager">{formatNumber(withdrawLimits.needToWager, 2)} SPERM</span>
                      </div>
                    )}
                  </div>

                  {/* 🔋 Moon Fuel — доступно к выводу */}
                  {withdrawFuel.total_staking > 0 && (
                    <div className="withdraw-limits-box" style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '10px' }}>
                      {(withdrawFuel as any).slot_exempt > 0 && (
                        <>
                          <div className="limit-item">
                            <span className="limit-label">💰 Свободный баланс</span>
                            <span className="limit-value available">{formatNumber((withdrawFuel as any).slot_exempt, 2)} SPERM</span>
                          </div>
                          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px', marginBottom: '8px' }}>
                            (депозит + слоты, без ограничений)
                          </div>
                        </>
                      )}
                      <div className="limit-item">
                        <span className="limit-label">🔋 Moon Fuel</span>
                        <span className="limit-value">{withdrawFuel.fuel_percent}%</span>
                      </div>
                      <div className="limit-item highlight">
                        <span className="limit-label">Доступно к выводу:</span>
                        <span className="limit-value available">{formatNumber(withdrawFuel.max_withdrawal, 2)} SPERM</span>
                      </div>
                      {withdrawFuel.fuel_percent < 100 && withdrawFuel.needed_for_100 > 0 && (
                        <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '6px' }}>
                          💡 Пригласи друзей на {withdrawFuel.needed_for_100} SPERM или пополни с кошелька
                        </div>
                      )}
                    </div>
                  )}

                  {/* Если баланс = 0 - показываем сообщение о пустом балансе */}
                  {withdrawLimits.balance <= 0 ? (
                    <div className="withdraw-blocked-box">
                      <div className="blocked-icon">💰</div>
                      <h3>{t('walletPage.zeroBalanceTitle', 'У вас 0 SPERM на балансе')}</h3>
                      <p>{t('walletPage.zeroBalanceDesc', 'Чтобы вывести средства:')}</p>
                      <ul>
                        <li>{t('walletPage.zeroBalanceOption1', '🐷 Проверьте копилку - там накапливается прибыль со стейкинга')}</li>
                        <li>{t('walletPage.zeroBalanceOption2', '💸 Переведите средства из копилки на основной баланс')}</li>
                      </ul>
                      <p className="hint warning">⚠️ {t('walletPage.zeroBalanceWarning', 'При переводе из копилки APR доходность сбросится до базового уровня!')}</p>
                    </div>
                  ) : (
                    /* Если баланс > 0 - показываем форму вывода */
                    <div className="withdraw-instructions">
                      <div className="instruction-step">
                        <span className="step-number">1</span>
                        <p>{t('home.minWithdraw')}</p>
                      </div>
                      <div className="instruction-step">
                        <span className="step-number">2</span>
                        <p>{t('home.withdrawTime')}</p>
                      </div>
                      {/* Если нужно прокрутить больше - показываем предупреждение */}
                      {withdrawLimits.needToWager > 0 && withdrawLimits.availableToWithdraw < withdrawLimits.balance && (
                        <div className="wager-info-box">
                          <p>🎰 {t('walletPage.wagerHint', 'Чтобы разблокировать весь баланс, прокрутите ещё {{amount}} SPERM в играх или в стейкинге', { amount: formatNumber(withdrawLimits.needToWager, 0) })}</p>
                        </div>
                      )}
                      <div className="withdraw-form">
                        <label>{t('home.withdrawAmount')} (max: {formatNumber(withdrawLimits.availableToWithdraw, 2)})</label>
                        <input
                          type="number"
                          placeholder={t('home.enterAmount')}
                          min="10"
                          max={withdrawLimits.availableToWithdraw}
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          disabled={withdrawLoading || withdrawLimits.availableToWithdraw < 10}
                        />
                        <div className="quick-amounts-row">
                          {[25, 50, 75, 100].map(percent => {
                            const amount = Math.floor(withdrawLimits.availableToWithdraw * percent / 100);
                            return amount >= 10 ? (
                              <button
                                key={percent}
                                className="quick-amount-btn"
                                onClick={() => setWithdrawAmount(amount.toString())}
                              >
                                {percent}%
                              </button>
                            ) : null;
                          })}
                        </div>
                        <button
                          className="withdraw-submit"
                          onClick={() => { playButtonClick(); handleWithdraw(); }}
                          disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) < 10 || parseFloat(withdrawAmount) > withdrawLimits.availableToWithdraw || withdrawLimits.availableToWithdraw < 10}
                        >
                          {withdrawLoading ? t('common.loading') : address ? (withdrawLimits.availableToWithdraw < 10 ? t('walletPage.notEnoughToWithdraw', 'Доступно менее 10 SPERM') : t('home.withdraw')) : t('home.connectWallet')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Содержимое для USDT */}
              {withdrawCurrency === 'USDT' && (
                <>
                  <div className="withdraw-limits-box usdt-box">
                    <div className="limit-item">
                      <span className="limit-label">{t('walletPage.withdrawBalanceLabel')} USDT:</span>
                      <span className="limit-value usdt-value">{formatNumber(balanceUsdt, 2)} USDT</span>
                    </div>
                    <div className="limit-item highlight">
                      <span className="limit-label">{t('walletPage.withdrawAvailableLabel')}</span>
                      <span className="limit-value available">{formatNumber(balanceUsdt, 2)} USDT</span>
                    </div>
                  </div>

                  {balanceUsdt <= 0 ? (
                    <div className="withdraw-blocked-box">
                      <div className="blocked-icon">💵</div>
                      <h3>{t('withdrawUsdt.noFunds', 'No USDT to withdraw')}</h3>
                      <p>{t('withdrawUsdt.noFundsDesc', 'USDT comes from staking and other income sources.')}</p>
                    </div>
                  ) : (
                    <div className="withdraw-instructions">
                      <div className="instruction-step">
                        <span className="step-number">1</span>
                        <p>{t('withdrawUsdt.minAmount', 'Minimum: 10 USDT')}</p>
                      </div>
                      <div className="instruction-step">
                        <span className="step-number">2</span>
                        <p>{t('home.withdrawTime')}</p>
                      </div>
                      <div className="withdraw-form">
                        <label>{t('withdrawUsdt.amountLabel', 'USDT amount')} (max: {formatNumber(balanceUsdt, 2)})</label>
                        <input
                          type="number"
                          placeholder={t('withdrawUsdt.placeholder', 'Enter USDT amount')}
                          min="10"
                          max={balanceUsdt}
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          disabled={withdrawLoading}
                        />
                        <div className="quick-amounts-row">
                          {[25, 50, 75, 100].map(percent => {
                            const amount = Math.floor(balanceUsdt * percent / 100);
                            return amount >= 10 ? (
                              <button
                                key={percent}
                                className="quick-amount-btn usdt-btn"
                                onClick={() => setWithdrawAmount(amount.toString())}
                              >
                                {percent}%
                              </button>
                            ) : null;
                          })}
                        </div>
                        <button
                          className="withdraw-submit usdt-submit"
                          onClick={() => { playButtonClick(); handleWithdraw(); }}
                          disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) < 10 || parseFloat(withdrawAmount) > balanceUsdt}
                        >
                          {withdrawLoading ? t('common.loading') : address ? t('withdrawUsdt.button', 'Withdraw USDT') : t('home.connectWallet')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => { playButtonClick(); setShowWithdrawModal(false); }}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* 🔐 Модалка 2FA для подтверждения вывода */}
      {show2FAModal && (
        <div className="modal-overlay" onClick={() => setShow2FAModal(false)}>
          <div className="modal-content twofa-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShow2FAModal(false)}>×</button>

            <div className="twofa-header">
              <div className="twofa-icon">🔐</div>
              <h3>{t('withdraw2fa.title', 'Подтверждение вывода')}</h3>
            </div>

            <div className="twofa-info">
              <div className="twofa-row">
                <span>{t('withdraw2fa.amount', 'Сумма')}:</span>
                <strong>{withdrawAmount} {withdrawCurrency}</strong>
              </div>
              <div className="twofa-row">
                <span>{t('withdraw2fa.address', 'Адрес')}:</span>
                <strong className="address-short">
                  {address ? `${address.slice(0, 8)}...${address.slice(-6)}` : '—'}
                </strong>
              </div>
            </div>

            {!codeSent ? (
              <div className="twofa-step1">
                <p className="twofa-desc">
                  {t('withdraw2fa.sendCodeDesc', 'Для безопасности вывода мы отправим 6-значный код в Telegram бот.')}
                </p>
                <button
                  className="action-button send-code-btn"
                  onClick={requestWithdrawalCode}
                  disabled={withdrawLoading || countdown > 0}
                >
                  {withdrawLoading ? t('common.loading', 'Загрузка...') : countdown > 0 ? `Подождите (${countdown}с)` : t('withdraw2fa.sendCode', '📱 Отправить код в Telegram')}
                </button>
              </div>
            ) : (
              <div className="twofa-step2">
                <p className="twofa-desc success">
                  {t('withdraw2fa.codeSent', '✅ Код отправлен в Telegram! Проверьте бота @moonraid_game_bot')}
                </p>

                <div className="code-input-wrapper">
                  <input
                    type="text"
                    className="code-input"
                    placeholder="000000"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                  />
                </div>

                {codeError && (
                  <div className="twofa-error">{codeError}</div>
                )}

                <button
                  className="action-button confirm-btn"
                  onClick={confirmWithdrawal}
                  disabled={withdrawLoading || verificationCode.length !== 6}
                >
                  {withdrawLoading ? t('common.loading', 'Проверка...') : t('withdraw2fa.confirm', '✅ Подтвердить вывод')}
                </button>

                <button
                  className="resend-btn"
                  onClick={requestWithdrawalCode}
                  disabled={countdown > 0 || withdrawLoading}
                >
                  {countdown > 0
                    ? t('withdraw2fa.resendIn', `Отправить повторно (${countdown}с)`)
                    : t('withdraw2fa.resend', '🔄 Отправить код повторно')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно с игрой */}
      <GameModal
        isOpen={showGameModal}
        onClose={() => {
          setShowGameModal(false);
          // Перезагружаем информацию о игре после закрытия
          loadDailyGame();
        }}
        gameId={selectedGameId}
      />

      {/* Модальное окно с детальным расчетом claim */}
      {lastClaimData && (
        <ClaimDetailsModal
          isOpen={showClaimDetails}
          onClose={() => setShowClaimDetails(false)}
          claimData={lastClaimData}
        />
      )}

      {/* Мини-квитанция после claim */}
      <ClaimReceiptModal
        isOpen={showClaimReceipt}
        onClose={() => setShowClaimReceipt(false)}
        claimDetails={claimReceiptDetails}
      />

    </div>
  );
};

export default HomePage;